import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Alert, TextField, FormControl, InputLabel, Select, MenuItem, OutlinedInput,
  Chip, FormControlLabel, Checkbox,
} from '@mui/material';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { internalTaskTypeService, InternalTaskType } from '../services/internalTaskTypeService';
import { userService, User } from '../services/userService';
import { ColumnConfig } from '../hooks/useTablePreferences';
import useTablePage from '../components/table/hooks/useTablePage';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import TablePageLayout from '../components/table/TablePageLayout';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';

const ASSIGN_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'doctor', label: 'Doctor' },
  { value: 'case_manager', label: 'Case Manager' },
  { value: 'nurse_prescriber', label: 'Nurse Prescriber' },
];
const assignTypeLabel = (v?: string | null) => ASSIGN_TYPE_OPTIONS.find(o => o.value === v)?.label || '-';

const DEFAULT_ACTION_OPTIONS: { value: string; label: string }[] = [
  { value: 'patient_page', label: 'Patient Page' },
  { value: 'edit_patient', label: 'Edit Patient' },
  { value: 'set_meeting', label: 'Set Meeting' },
  { value: 'meeting_summary', label: 'Meeting Summary' },
];
const defaultActionLabel = (v?: string | null) => DEFAULT_ACTION_OPTIONS.find(o => o.value === v)?.label || '-';

const columns: ColumnConfig[] = [
  { key: 'name', label: 'Name', defaultWidth: 220, minWidth: 140, visible: true, sortable: true, resizable: true, draggable: true },
  { key: 'assignees', label: 'Default Assignees', defaultWidth: 240, minWidth: 140, visible: true, sortable: false, resizable: true, draggable: true },
  { key: 'assignType', label: 'Fallback Assignee', defaultWidth: 150, minWidth: 110, visible: true, sortable: false, resizable: true, draggable: true },
  { key: 'meeting', label: 'Meeting Dropdown', defaultWidth: 150, minWidth: 110, visible: true, sortable: false, resizable: true, draggable: true },
  { key: 'defaultAction', label: 'Default Action', defaultWidth: 150, minWidth: 110, visible: true, sortable: false, resizable: true, draggable: true },
  { key: 'sortOrder', label: 'Order', defaultWidth: 90, minWidth: 70, visible: true, sortable: true, resizable: true, draggable: true },
];

const InternalTaskTypesPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const [users, setUsers] = useState<User[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<InternalTaskType | null>(null);
  const [name, setName] = useState('');
  const [assignees, setAssignees] = useState<number[]>([]);
  const [assignType, setAssignType] = useState('');
  const [showMeeting, setShowMeeting] = useState(false);
  const [defaultAction, setDefaultAction] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);

  const getSortValue = useCallback((t: InternalTaskType, key: string): any => {
    switch (key) {
      case 'name': return t.name.toLowerCase();
      case 'sortOrder': return t.sort_order;
      default: return '';
    }
  }, []);
  const getSearchText = useCallback((t: InternalTaskType): string[] => [t.name], []);

  const table = useTablePage<InternalTaskType>({
    entityType: 'internalTaskTypes', columns, tenantId: currentTenant?.id, getSortValue, getSearchText,
  });

  const reload = useCallback(() => {
    if (!currentTenant?.id) return;
    table.loadData(() => internalTaskTypeService.getAll(currentTenant.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.id]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (currentTenant?.id) userService.getUsers(currentTenant.id).then(setUsers).catch(() => {});
  }, [currentTenant?.id]);

  const openCreate = () => {
    setEditing(null); setName(''); setAssignees([]); setAssignType(''); setShowMeeting(false); setDefaultAction(''); setSortOrder(0);
    setDialogOpen(true);
  };
  const openEdit = (t: InternalTaskType) => {
    setEditing(t); setName(t.name); setAssignees(t.default_assignee_ids || []);
    setAssignType(t.assign_type || ''); setShowMeeting(t.show_meeting_dropdown);
    setDefaultAction(t.default_action || ''); setSortOrder(t.sort_order);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim() || !currentTenant) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        default_assignee_ids: assignees,
        assign_type: assignType || null,
        show_meeting_dropdown: showMeeting,
        default_action: defaultAction || null,
        sort_order: sortOrder,
      };
      if (editing) await internalTaskTypeService.update(editing.id, payload);
      else await internalTaskTypeService.create(currentTenant.id, payload);
      toast.success('Saved');
      setDialogOpen(false);
      reload();
    } catch {
      toast.error('Could not save task type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm('Delete this task type?')) return;
    try {
      await internalTaskTypeService.remove(editing.id);
      toast.success('Deleted');
      setDialogOpen(false);
      reload();
    } catch {
      toast.error('Could not delete');
    }
  };

  const getColumnValue = (t: InternalTaskType, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name': return t.name;
      case 'assignees': return (t.default_assignee_ids || []).map(id => users.find(u => u.id === id)?.full_name || id).join(', ') || '-';
      case 'assignType': return assignTypeLabel(t.assign_type);
      case 'meeting': return t.show_meeting_dropdown ? 'Yes' : 'No';
      case 'defaultAction': return defaultActionLabel(t.default_action);
      case 'sortOrder': return t.sort_order;
      default: return '';
    }
  };

  return (
    <TablePageLayout loading={false} error={null}>
      <DataTableToolbar
        {...table.toolbarProps}
        title="Task Types"
        searchPlaceholder="Search task types..."
        isAdmin={!!user?.user_privilege?.task_types}
        actions={<Button variant="contained" onClick={openCreate} sx={CLINIC_DIALOG_SX.primaryButton}>New Task Type</Button>}
      />

      {table.error && <Alert severity="error" sx={{ mb: 2 }}>{table.error}</Alert>}

      {table.loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={table.sortedData}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            getRowKey={(t) => t.id}
            onRowClick={(_e, t) => openEdit(t)}
          />
        </DataTableContainer>
      )}

      <ClinicDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? 'Edit Task Type' : 'New Task Type'}
        maxWidth="sm"
        actions={
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', width: '100%' }}>
            <Box>{editing && <Button color="error" onClick={handleDelete} sx={CLINIC_DIALOG_SX.cancelButton}>Delete</Button>}</Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving} sx={CLINIC_DIALOG_SX.primaryButton}>{saving ? 'Saving…' : 'Save'}</Button>
              <Button onClick={() => setDialogOpen(false)} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
            </Box>
          </Box>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth size="small" />
          <FormControl fullWidth size="small">
            <InputLabel>Default Assignees</InputLabel>
            <Select
              multiple value={assignees} onChange={(e) => setAssignees(e.target.value as number[])}
              input={<OutlinedInput label="Default Assignees" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map((id) => <Chip key={id} size="small" label={users.find(u => u.id === id)?.full_name || id} />)}
                </Box>
              )}
            >
              {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Fallback assignee (when no default assignees)</InputLabel>
            <Select value={assignType} label="Fallback assignee (when no default assignees)" onChange={(e) => setAssignType(e.target.value)}>
              <MenuItem value="">(none)</MenuItem>
              {ASSIGN_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox checked={showMeeting} onChange={(e) => setShowMeeting(e.target.checked)} />}
            label="Show meeting dropdown on the task form"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Default action button</InputLabel>
            <Select value={defaultAction} label="Default action button" onChange={(e) => setDefaultAction(e.target.value)}>
              <MenuItem value="">(none)</MenuItem>
              {DEFAULT_ACTION_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Sort order" type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} sx={{ width: 140 }} size="small" />
        </Box>
      </ClinicDialog>
    </TablePageLayout>
  );
};

export default InternalTaskTypesPage;
